import withRouter from "Components/Common/withRouter"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { createSelector } from "reselect"
import { ColumnDef } from "@tanstack/react-table"
import useDataTable from "hooks/useDataTable"
import React from "react"
import DataTableTemplate from "Components/templates/data-table-template"
import { getDepartmentBudgets, getDepartmentBudgetsSearchFields } from "slices/thunk"
import { downloadDepartmentBudgetsCSV } from "slices/finance/department-budget/thunk"
import FiscalCalendar, { DEFAULT_FISCAL_START } from "utils/fiscal-calendar"
import { formatCurrency } from "utils/tools"
import SearchSelect from "Components/molecule/inputs/search-select"
import SearchBudgetLinesForm from "Components/organisms/forms/search-department-budgets-form"
import { IBudgetLineQueryParam } from "models/budget-line"
import { DepartmentBudgetsSliceState, setDepartmentBudgetsLoading } from "slices/finance/department-budget/reducer"
import Branch from "models/branch"
import useManual from "hooks/useManual"
import useFiscalYearOptions from "hooks/useFiscalYearOptions"
import ActionButton from "Components/atoms/buttons/action-button"
import { HelpIcon } from "Components/atoms/icons"
import { compareText, stripDepartmentPrefix, toDepartmentOptions } from "utils/department-options"
import TreeNameCell from "Components/atoms/tree-name-cell"
import Department from "models/department"
import { getExpandedRowModel } from "@tanstack/react-table"

const DEFAULT_FISCAL_YEAR = FiscalCalendar.lastMonthFiscalYear()

const selectBudgets = createSelector(
    (state: any) => state.SubAccountBudgets,
    ({
        budgets,
        isLoading,
        branches,
        departments
    }: DepartmentBudgetsSliceState) => ({
        budgets,
        isLoading,
        branches,
        departments
    })
)

type TableRow = {
    id: string
    code: string
    description: string
    parentId?: string | null
    periods: Record<number, number>
    total: number
    // Departments under a division row
    subRows?: TableRow[]
}

const emptyPeriods = (): Record<number, number> => {
    const periods: Record<number, number> = {}
    for (let p = 1; p <= 12; p++) periods[p] = 0
    return periods
}

const DepartmentBudgetsPage = () => {
    document.title = "Department Budgets | IbexIQ"

    const { handleOpenManual } = useManual("finance/department-budgets");


    const dispatch: any = useDispatch()
    const {
        budgets,
        isLoading,
        departments,
        branches,
    } = useSelector(selectBudgets)
    const [fiscalYear, setFiscalYear] = useState<number>(DEFAULT_FISCAL_YEAR)
    const { options: fiscalYearOptions, defaultYear, isLoading: isLoadingFiscalYears } = useFiscalYearOptions()
    const [isExporting, setIsExporting] = useState(false)
    const [query, setQuery] = useState<IBudgetLineQueryParam>({
        page: 0,
        limit: Number.MAX_SAFE_INTEGER,
        financialYear: DEFAULT_FISCAL_YEAR
    })
    const calendar = new FiscalCalendar(DEFAULT_FISCAL_START)

    const init = async () => {
        const pms: any[] = [];

        // Get search fields
        pms.push(dispatch(getDepartmentBudgetsSearchFields()))
        pms.push(dispatch(getDepartmentBudgets(query)))

        await Promise.all(pms)
    }

    useEffect(() => {
        init()
    }, [fiscalYear])

    const tableData: TableRow[] = useMemo(() => {
        if (!budgets) return []

        return budgets.map((sub: any) => {
            const periodTotals: Record<number, number> = {}
            for (let p = 1; p <= 12; p++) periodTotals[p] = 0

            let total = 0

            sub.budgetLines?.forEach((line: any) => {
                line.details?.forEach((d: any) => {
                    const amount = Number(d.amount ?? 0)
                    const period = d.fiscalPeriod % 100
                    periodTotals[period] += amount
                    total += amount
                })
            })

            return {
                id: sub.id,
                code: sub.code,
                description: stripDepartmentPrefix(sub.description),
                parentId: sub.parentId,
                periods: periodTotals,
                total,
            }
        })
    }, [budgets])

    // Same layout as Consulting Income: division rows (sum of their departments) with the
    // departments nested and indented, all A-Z. Departments without a known division stay on top level.
    const treeRows: TableRow[] = useMemo(() => {
        const byName = (a: TableRow, b: TableRow) => compareText(a.description, b.description)
        const divisions: TableRow[] = departments
            .map((d: Department) => {
                const children = tableData.filter(r => r.parentId === d.id).sort(byName)
                const periods = emptyPeriods()
                children.forEach(c => { for (let p = 1; p <= 12; p++) periods[p] += c.periods[p] })
                return {
                    id: `division-${d.id}`,
                    code: d.code,
                    description: stripDepartmentPrefix(d.description),
                    periods,
                    total: children.reduce((sum, c) => sum + c.total, 0),
                    subRows: children,
                }
            })
            .filter((d: TableRow) => d.subRows!.length > 0)
        const divisionIds = new Set(departments.map((d: Department) => d.id))
        const orphans = tableData.filter(r => !r.parentId || !divisionIds.has(r.parentId))
        return [...divisions, ...orphans].sort(byName)
    }, [tableData, departments])

    const handleChangeQuery = (
        key: string,
        value: any
    ) => {
        query[key] = value
        const q = {
            ...query,
        }
        setQuery(q);
    };

    // If the current fiscal year is hidden, move to the newest visible year (only if the user hasn't picked one).
    // Changing fiscalYear re-runs init(), which fetches with the updated query.
    useEffect(() => {
        if (!isLoadingFiscalYears && defaultYear !== DEFAULT_FISCAL_YEAR && Number(query.financialYear) === DEFAULT_FISCAL_YEAR) {
            handleChangeQuery('financialYear', defaultYear)
            setFiscalYear(defaultYear)
        }
    }, [isLoadingFiscalYears, defaultYear])

    const handleClearQuery = () => {
        const q = {
            page: query.page,
            limit: query.limit,
            financialYear: query.financialYear
        }

        setQuery(q);
    }

    const handleSearch = useCallback(async () => {
        await dispatch(getDepartmentBudgets(query));
    }, [query, getDepartmentBudgets, setDepartmentBudgetsLoading])


    const periodTotals: Record<number, number> = useMemo(() => {
        const totals: Record<number, number> = {}
        for (let p = 1; p <= 12; p++) totals[p] = 0
        tableData.forEach((row) => {
            for (let p = 1; p <= 12; p++) {
                totals[p] += row.periods[p]
            }
        })
        return totals
    }, [tableData])

    const grandTotal = useMemo(
        () => Object.values(periodTotals).reduce((sum, v) => sum + v, 0),
        [periodTotals]
    )

    const handleExportData = () => {
        setIsExporting(true)
        try {
            downloadDepartmentBudgetsCSV(tableData, periodTotals, grandTotal, query.financialYear ?? fiscalYear)
        } finally {
            setIsExporting(false)
        }
    }

    const columns = React.useMemo<ColumnDef<TableRow>[]>(
        () => [
            {
                header: "Division / Department",
                accessorKey: "description",
                id: "name",
                size: 260,
                meta: {
                    getFooterStyle: () => ({
                        background: "var(--bs-primary)",
                        color: "var(--bs-white)",
                        fontWeight: 600,
                    })
                },
                // One line: name, then the code in muted text (full text on hover)
                cell: ({ row }) => (
                    <TreeNameCell row={row}>
                        <span title={`${row.original.description} (${row.original.code})`}>
                            {row.original.description} <span className="text-muted small">{row.original.code}</span>
                        </span>
                    </TreeNameCell>
                ),
                footer: () => "Total",
            },
            ...[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((p) => ({
                header: calendar.periodToMonth(p, "en-CA"),
                accessorFn: (row: TableRow) => row.periods[p],
                id: `period-${p}`,
                size: 95,
                meta: {
                    getCellStyle: () => ({
                        padding: "5px",
                        textAlign: 'right'
                    }),
                    getFooterStyle: () => ({
                        background: "var(--bs-primary)",
                        color: "var(--bs-white)",
                        fontWeight: 600,
                        textAlign: 'right'
                    }),
                    getHeaderStyle: () => ({
                        textAlign: "right",
                    }),
                },
                cell: ({ getValue }: any) => formatCurrency(Number(getValue() ?? 0)),
                footer: () => (formatCurrency(periodTotals[p])),
            })),
            {
                header: "Total",
                accessorKey: "total",
                id: "total",
                size: 110,
                meta: {
                    getCellStyle: () => ({
                        background: "var(--bs-secondary-bg)",
                        textAlign: 'right',
                        padding: "5px",
                    }),
                    getFooterStyle: () => ({
                        background: "var(--bs-primary)",
                        color: "var(--bs-white)",
                        fontWeight: 600,
                        textAlign: 'right'
                    }),
                    getHeaderStyle: () => ({
                        textAlign: "right",
                    }),
                },
                cell: ({ getValue }: any) => formatCurrency(getValue()),
                footer: () => formatCurrency(grandTotal),
            },
        ],
        [fiscalYear, periodTotals, grandTotal]
    )

    // Has a totals footer: no sorting
    const table = useDataTable({
        data: treeRows,
        columns,
        getRowId: (row) => row.id,
        getSubRows: (row) => row.subRows,
        getExpandedRowModel: getExpandedRowModel(),
        initialState: { expanded: true },
        enableSorting: false,
    })

    return (
        <DataTableTemplate
            fullExport={handleExportData}
            title={"Finance"}
            breadcrumbItem={"Department Budgets"}
            actions={
                <ActionButton
                    onClick={handleOpenManual}
                >
                    Help <HelpIcon />
                </ActionButton>
            }

            table={table}
            columns={columns}
            isLoading={isLoading}
            filters={
                <SearchBudgetLinesForm
                    handleChangeQuery={handleChangeQuery}
                    query={query}
                    branches={[{ value: '', label: 'All' }, ...branches.map((b: Branch) => ({ value: b.id, label: b.name }))]}
                    departments={[{ value: '', label: 'All' }, ...toDepartmentOptions(departments)]}
                    handleClearSearch={handleClearQuery}
                    handleSearch={handleSearch}
                    isSearching={false}
                />
            }
            tableActions={
                <>
                    <SearchSelect
                        label="Fiscal Year"
                        value={query.financialYear?.toString()}
                        onChange={(e) => {
                            handleChangeQuery('financialYear', e?.value)
                        }}
                        options={fiscalYearOptions}
                        inputStyle={{ margin: 0, minWidth: "150px" }}
                    />
                </>
            }
        />
    )
}

export default withRouter(DepartmentBudgetsPage)